import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, ShoppingBag, Trash2, Tag, Loader2, CheckCircle, Truck, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { validatePromoCode, createOrder, updateMe } from '../services/api';
import { shippingSettings } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    applyPromoCode,
    removePromoCode,
    appliedPromo,
    subtotal,
    discountAmount,
    total,
    getOrderData
  } = useCart();

  const { user, isAuthenticated } = useAuth();

  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [orderError, setOrderError] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery'); // 'delivery' or 'pickup'
  const [pickupLocation, setPickupLocation] = useState('');
  const [dynamicFreeShipping, setDynamicFreeShipping] = useState(shippingSettings.freeShippingThreshold);
  
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    note: ''
  });

  // Učitaj dinamičku vrednost za besplatnu dostavu kad se korpa otvori
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-settings.php`);
        if (res.ok) {
          const data = await res.json();
          if (data.free_shipping_threshold) {
            setDynamicFreeShipping(parseInt(data.free_shipping_threshold));
          }
        }
      } catch (e) {
        // Koristi default vrednost
      }
    };
    if (isCartOpen) {
      fetchSettings();
    }
  }, [isCartOpen]);

  // Shipping calculations
  const { shippingCost, pickupLocations } = shippingSettings;
  const freeShippingThreshold = dynamicFreeShipping;
  const amountForFreeShipping = freeShippingThreshold - total;
  const isFreeShipping = total >= freeShippingThreshold;
  const actualShippingCost = deliveryMethod === 'pickup' ? 0 : (isFreeShipping ? 0 : shippingCost);
  const finalTotal = total + actualShippingCost;

  // Učitaj podatke korisnika ako je prijavljen
  useEffect(() => {
    if (isAuthenticated && user) {
      setCustomer(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || ''
      }));
    }
  }, [isAuthenticated, user, showCheckout]);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const response = await validatePromoCode(promoInput);
      if (response.valid) {
        applyPromoCode(promoInput.toUpperCase(), response.discount);
        setPromoInput('');
      } else {
        setPromoError(response.message);
      }
    } catch (err) {
      setPromoError('Greška pri proveri promo koda');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setOrderLoading(true);
    setOrderError('');
    
    try {
      // Add delivery info to note
      let deliveryNote = customer.note;
      if (deliveryMethod === 'pickup') {
        const location = pickupLocations.find(l => l.id === pickupLocation);
        deliveryNote = `LIČNO PREUZIMANJE: ${location?.name} (${location?.address})${deliveryNote ? '\n' + deliveryNote : ''}`;
      }
      
      const orderData = getOrderData({
        ...customer,
        note: deliveryNote,
        shipping_cost: actualShippingCost
      });
      orderData.shipping_cost = actualShippingCost;
      orderData.delivery_method = deliveryMethod;
      orderData.total = finalTotal;
      
      const order = await createOrder(orderData);
      setOrderSuccess({ ...order, shipping_cost: actualShippingCost, final_total: finalTotal });
      clearCart();
      
      // Sačuvaj podatke za dostavu u profil korisnika
      if (isAuthenticated && user) {
        try {
          await updateMe({
            name: customer.name,
            phone: customer.phone,
            address: customer.address,
            city: customer.city,
            postal_code: customer.postal_code
          });
        } catch (e) {
          console.log('Could not save delivery info to profile');
        }
      }
    } catch (err) {
      console.error('Order error:', err);
      // Prikaži tačnu poruku greške sa servera
      const errorMessage = err.response?.data?.error || err.message || 'Greška pri kreiranju narudžbine. Molimo pokušajte ponovo.';
      setOrderError(errorMessage);
    } finally {
      setOrderLoading(false);
    }
  };

  const resetAndClose = () => {
    setShowCheckout(false);
    setOrderSuccess(null);
    setOrderError('');
    // Ne resetuj podatke ako je korisnik prijavljen
    if (!isAuthenticated) {
      setCustomer({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        note: ''
      });
    }
    setIsCartOpen(false);
  };

  if (!isCartOpen) return null;

  // Order Success View
  if (orderSuccess) {
    return (
      <>
        <div className="cart-overlay" onClick={resetAndClose} />
        <div className="cart-drawer">
          <div className="cart-header">
            <h2 className="cart-title">
              <CheckCircle size={22} className="success-icon" />
              Porudžbina primljena
            </h2>
            <button className="cart-close" onClick={resetAndClose}>
              <X size={24} />
            </button>
          </div>
          
          <div className="order-success">
            <div className="success-content">
              {/* Logo */}
              <div className="order-logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_puter-klub/artifacts/wdaauptq_Logo_V%20%281%29.png" 
                  alt="Ganetov Premium Puter" 
                />
              </div>
              
              <div className="order-success-badge">
                <CheckCircle size={32} />
              </div>
              
              <h3>Hvala Vam na porudžbini!</h3>
              
              <div className="order-id-box">
                <span className="order-id-label">Broj porudžbine</span>
                <span className="order-id-value">{orderSuccess.id}</span>
              </div>
              
              <p className="order-email-note">
                Potvrda je poslata na Vašu email adresu.
              </p>
              
              <div className="order-summary-final">
                <div className="summary-header">Detalji porudžbine</div>
                
                <div className="summary-row">
                  <span>Proizvodi:</span>
                  <span>{orderSuccess.subtotal?.toLocaleString('sr-RS') || subtotal.toLocaleString('sr-RS')} RSD</span>
                </div>
                
                {orderSuccess.discount > 0 && (
                  <div className="summary-row discount">
                    <span>Popust:</span>
                    <span>-{orderSuccess.discount.toLocaleString('sr-RS')} RSD</span>
                  </div>
                )}
                
                <div className="summary-row">
                  <span>Dostava:</span>
                  <span className={orderSuccess.shipping_cost === 0 ? 'free-delivery' : ''}>
                    {orderSuccess.shipping_cost === 0 ? 'Besplatno' : `${orderSuccess.shipping_cost?.toLocaleString('sr-RS')} RSD`}
                  </span>
                </div>
                
                <div className="summary-divider"></div>
                
                <div className="summary-row total">
                  <span>Ukupno za plaćanje:</span>
                  <span>{(orderSuccess.final_total || orderSuccess.total).toLocaleString('sr-RS')} RSD</span>
                </div>
              </div>
              
              <div className="order-next-steps">
                <p>Kontaktiraćemo Vas uskoro radi potvrde i dogovora o dostavi.</p>
              </div>
              
              <button className="checkout-btn order-close-btn" onClick={resetAndClose}>
                Nastavi kupovinu
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Checkout Form View
  if (showCheckout && cartItems.length > 0) {
    return (
      <>
        <div className="cart-overlay" onClick={() => setShowCheckout(false)} />
        <div className="cart-drawer">
          <div className="cart-header">
            <h2 className="cart-title">
              <ShoppingBag size={22} />
              Završi porudžbinu
            </h2>
            <button className="cart-close" onClick={() => setShowCheckout(false)}>
              <X size={24} />
            </button>
          </div>
          
          <form className="checkout-form" onSubmit={handleSubmitOrder}>
            <div className="form-group">
              <label htmlFor="name">Ime i prezime *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={customer.name}
                onChange={handleCustomerChange}
                required
                placeholder="Vaše ime i prezime"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={customer.email}
                onChange={handleCustomerChange}
                required
                placeholder="vas@email.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Telefon *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={customer.phone}
                onChange={handleCustomerChange}
                required
                placeholder="+381 60 123 4567"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address">Adresa *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={customer.address}
                onChange={handleCustomerChange}
                required
                placeholder="Ulica i broj"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">Grad *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={customer.city}
                  onChange={handleCustomerChange}
                  required
                  placeholder="Novi Sad"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="postal_code">Poštanski broj</label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={customer.postal_code}
                  onChange={handleCustomerChange}
                  placeholder="21000"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="note">Napomena</label>
              <textarea
                id="note"
                name="note"
                value={customer.note}
                onChange={handleCustomerChange}
                placeholder="Dodatne napomene za dostavu..."
                rows={3}
              />
            </div>

            {/* Delivery Method Selection */}
            <div className="delivery-method-section">
              <label className="section-label">Način preuzimanja</label>
              <div className="delivery-options">
                <label className={`delivery-option ${deliveryMethod === 'delivery' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="delivery"
                    checked={deliveryMethod === 'delivery'}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                  />
                  <Truck size={20} />
                  <div className="option-info">
                    <span className="option-title">Dostava na adresu</span>
                    <span className="option-price">
                      {isFreeShipping ? 'Besplatno' : `${shippingCost} RSD`}
                    </span>
                  </div>
                </label>
                <label className={`delivery-option ${deliveryMethod === 'pickup' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="pickup"
                    checked={deliveryMethod === 'pickup'}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                  />
                  <MapPin size={20} />
                  <div className="option-info">
                    <span className="option-title">Lično preuzimanje</span>
                    <span className="option-price">Besplatno</span>
                  </div>
                </label>
              </div>

              {deliveryMethod === 'pickup' && (
                <div className="pickup-locations">
                  <label>Izaberite lokaciju *</label>
                  <select 
                    value={pickupLocation} 
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required={deliveryMethod === 'pickup'}
                  >
                    <option value="">-- Izaberite grad --</option>
                    {pickupLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            <div className="cart-summary">
              <div className="summary-row">
                <span>Proizvodi:</span>
                <span>{subtotal.toLocaleString('sr-RS')} RSD</span>
              </div>
              {appliedPromo && (
                <div className="summary-row discount">
                  <span>Popust ({appliedPromo.discount}%):</span>
                  <span>-{discountAmount.toLocaleString('sr-RS')} RSD</span>
                </div>
              )}
              <div className="summary-row">
                <span>Dostava:</span>
                <span>{actualShippingCost === 0 ? 'Besplatno' : `${actualShippingCost.toLocaleString('sr-RS')} RSD`}</span>
              </div>
              <div className="summary-row total">
                <span>Za plaćanje:</span>
                <span>{finalTotal.toLocaleString('sr-RS')} RSD</span>
              </div>
            </div>
            
            {orderError && <p className="order-error">{orderError}</p>}
            
            <div className="cart-actions">
              <button 
                type="submit" 
                className="checkout-btn"
                disabled={orderLoading}
              >
                {orderLoading ? (
                  <>
                    <Loader2 size={18} className="loading-spinner" />
                    Obrada...
                  </>
                ) : (
                  'Potvrdi narudžbinu'
                )}
              </button>
              <button 
                type="button" 
                className="clear-cart" 
                onClick={() => setShowCheckout(false)}
              >
                Nazad na korpu
              </button>
            </div>
          </form>
        </div>
      </>
    );
  }

  // Main Cart View
  return (
    <>
      <div className="cart-overlay" onClick={() => setIsCartOpen(false)} />
      <div className="cart-drawer">
        <div className="cart-header">
          <h2 className="cart-title">
            <ShoppingBag size={22} />
            Vaša korpa
          </h2>
          <button
            className="cart-close"
            onClick={() => setIsCartOpen(false)}
            aria-label="Zatvori korpu"
          >
            <X size={24} />
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty">
            <ShoppingBag size={48} />
            <p>Vaša korpa je prazna</p>
            <button
              className="continue-shopping"
              onClick={() => setIsCartOpen(false)}
            >
              Nastavi kupovinu
            </button>
          </div>
        ) : (
          <>
            {/* Free Shipping Progress Bar */}
            <div className="free-shipping-bar">
              {isFreeShipping ? (
                <div className="free-shipping-success">
                  <CheckCircle size={18} />
                  <span>Čestitamo! Ostvarili ste besplatnu dostavu!</span>
                </div>
              ) : (
                <>
                  <div className="free-shipping-text">
                    <Truck size={18} />
                    <span>Do besplatne dostave fali vam još <strong>{amountForFreeShipping.toLocaleString('sr-RS')} RSD</strong></span>
                  </div>
                  <div className="free-shipping-progress">
                    <div 
                      className="free-shipping-progress-fill" 
                      style={{ width: `${Math.min((total / freeShippingThreshold) * 100, 100)}%` }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="cart-items">
              {cartItems.map((item) => (
                <div key={item.id} className="cart-item">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="cart-item-image"
                  />
                  <div className="cart-item-details">
                    <h4 className="cart-item-name">{item.name}</h4>
                    <p className="cart-item-size">{item.size}</p>
                    <p className="cart-item-price">
                      {item.price.toLocaleString('sr-RS')} RSD
                    </p>
                  </div>
                  <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        aria-label="Smanji količinu"
                      >
                        <Minus size={14} />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        aria-label="Povećaj količinu"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      className="remove-item"
                      onClick={() => removeFromCart(item.id)}
                      aria-label="Ukloni iz korpe"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-promo">
              {appliedPromo ? (
                <div className="promo-applied">
                  <Tag size={16} />
                  <span>{appliedPromo.code} (-{appliedPromo.discount}%)</span>
                  <button onClick={removePromoCode}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="promo-input-group">
                  <input
                    type="text"
                    placeholder="Promo kod"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    className="promo-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                  />
                  <button 
                    className="apply-promo" 
                    onClick={handleApplyPromo}
                    disabled={promoLoading}
                  >
                    {promoLoading ? <Loader2 size={14} className="loading-spinner" /> : 'Primeni'}
                  </button>
                </div>
              )}
              {promoError && <p className="promo-error">{promoError}</p>}
            </div>

            <div className="cart-summary">
              <div className="summary-row">
                <span>Proizvodi:</span>
                <span>{subtotal.toLocaleString('sr-RS')} RSD</span>
              </div>
              {appliedPromo && (
                <div className="summary-row discount">
                  <span>Popust ({appliedPromo.discount}%):</span>
                  <span>-{discountAmount.toLocaleString('sr-RS')} RSD</span>
                </div>
              )}
              <div className="summary-row">
                <span>Dostava:</span>
                <span>{isFreeShipping ? 'Besplatno' : `${shippingCost.toLocaleString('sr-RS')} RSD`}</span>
              </div>
              <div className="summary-row total">
                <span>Za plaćanje:</span>
                <span>{(total + (isFreeShipping ? 0 : shippingCost)).toLocaleString('sr-RS')} RSD</span>
              </div>
            </div>

            <div className="cart-actions">
              <button className="checkout-btn" onClick={() => setShowCheckout(true)}>
                Nastavi ka plaćanju
              </button>
              <button className="clear-cart" onClick={clearCart}>
                Isprazni korpu
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
